import React, { useState } from "react";
import { Menu, X, Rocket, User } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect } from "react";
import { connect } from "react-redux";

import { siteConfig } from "config/site";
import { Button } from "views/components/ui/Button";
import { SearchBar } from "views/components/SearchBar";
import UserDropdown from "./UserDropdown";
import {
    Drawer,
    DrawerContent,
    DrawerHeader,
    DrawerTitle,
} from "views/components/ui/Drawer";
import { Logo } from "views/common/Logo";

const headerLinks = [
    { name: "Submit", href: "/submit" },
    { name: "Category", href: "/category" },
    { name: "Blog", href: "/blog/1" },
];

const SiteHeader = ({ userAuth }) => {
    const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
    const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);

    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth >= 768) {
                setIsMobileNavOpen(false);
            }
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isUserDropdownOpen && !event.target.closest(".user-dropdown")) {
                setIsUserDropdownOpen(false);
            }
        };

        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, [isUserDropdownOpen]);

    const renderUserAvatar = () => {
        if (userAuth?.userAccount?.profile_image?.url) {
            return (
                <img
                    src={userAuth.userAccount.profile_image.url}
                    alt={userAuth.userAccount.preferred_name}
                    className="h-8 w-8 rounded-full object-cover"
                />
            );
        }
        return (
            <div className="h-8 w-8 rounded-full bg-gray-100 flex items-center justify-center">
                <User className="h-5 w-5 text-gray-500" />
            </div>
        );
    };

    return (
        <header className="sticky top-0 z-50 w-full border-b bg-white">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex h-16 items-center justify-between">
                    <div className="flex items-center space-x-8">
                        {/* Logo */}
                        <Logo />

                        {/* Desktop Navigation */}
                        <nav className="hidden md:flex items-center space-x-6">
                            {headerLinks.map((link) => (
                                <Link
                                    key={link.name}
                                    to={link.href}
                                    className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                                >
                                    {link.name}
                                </Link>
                            ))}
                        </nav>
                    </div>

                    {/* Search Bar */}
                    <div className="hidden md:block w-96">
                        <SearchBar />
                    </div>

                    {/* Right Side Actions */}
                    <div className="flex items-center space-x-4">
                        {userAuth?.isLoggedIn ? (
                            <div className="relative user-dropdown">
                                <button
                                    onClick={() =>
                                        setIsUserDropdownOpen(
                                            !isUserDropdownOpen
                                        )
                                    }
                                    className="flex items-center space-x-2 focus:outline-none"
                                >
                                    {renderUserAvatar()}
                                </button>
                                {isUserDropdownOpen && (
                                    <UserDropdown userAuth={userAuth} />
                                )}
                            </div>
                        ) : (
                            <Button
                                variant="outline"
                                size="sm"
                                asChild
                                className="hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 transition-colors"
                            >
                                <Link to="/sign-in">Sign In</Link>
                            </Button>
                        )}

                        {/* Mobile Menu Button */}
                        <Button
                            variant="ghost"
                            size="icon"
                            className="md:hidden h-9 w-9 rounded-full hover:bg-gray-100"
                            onClick={() => setIsMobileNavOpen(!isMobileNavOpen)}
                        >
                            {isMobileNavOpen ? (
                                <X className="h-5 w-5" />
                            ) : (
                                <Menu className="h-5 w-5" />
                            )}
                            <span className="sr-only">
                                {isMobileNavOpen ? "Close menu" : "Open menu"}
                            </span>
                        </Button>
                    </div>
                </div>
            </div>

            {/* Mobile Navigation Drawer */}
            <Drawer open={isMobileNavOpen} onOpenChange={setIsMobileNavOpen}>
                <DrawerContent className="bg-white">
                    <DrawerHeader>
                        <DrawerTitle>Menu</DrawerTitle>
                    </DrawerHeader>
                    <div className="flex flex-col space-y-4 p-4">
                        <div className="mb-4">
                            <SearchBar />
                        </div>
                        {headerLinks.map((link) => (
                            <Link
                                key={link.name}
                                to={link.href}
                                className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                            >
                                {link.name}
                            </Link>
                        ))}
                        {userAuth?.isLoggedIn && (
                            <>
                                <Link
                                    to="/dashboard"
                                    className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                                >
                                    Dashboard
                                </Link>
                                <Link
                                    to="/settings"
                                    className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
                                >
                                    Settings
                                </Link>
                            </>
                        )}
                    </div>
                </DrawerContent>
            </Drawer>
        </header>
    );
};

const mapStateToProps = (state) => ({
    userAuth: state.auth.userAuth,
});

export default connect(mapStateToProps)(SiteHeader);
